<template>
    <div class="period-bar">
        <v-menu offset-y nudge-bottom="6" transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
                <button type="button" class="period-year" v-on="on" v-bind="attrs">
                    {{ year }}
                    <v-icon size="14">mdi-chevron-down</v-icon>
                </button>
            </template>
            <v-list dense color="surface" class="py-1 period-year-list">
                <v-list-item v-for="option in years" :key="option" @click="selectYear(option)">
                    <v-list-item-title class="period-year-option" :class="{ active: option === year }">
                        {{ option }}
                    </v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <div class="period-months">
            <button v-for="item in months" :key="item.value" type="button" class="period-month"
                :class="{ active: item.value === month }" :disabled="item.disabled"
                @click="selectMonth(item.value)">
                {{ item.label }}
            </button>
        </div>

        <div v-if="$slots.actions" class="period-actions">
            <slot name="actions" />
        </div>
    </div>
</template>

<script>
const YEARS_BACK = 3

export default {
    name: "PeriodBar",
    props: {
        value: {
            type: String,
            default: '',
        },
        maxPeriod: {
            type: String,
            default: '',
        },
    },
    computed: {
        period(vm = this) {
            return vm.value || vm.$moment().format('YYYY-MM')
        },
        year(vm = this) {
            return Number(vm.period.slice(0, 4))
        },
        month(vm = this) {
            return Number(vm.period.slice(5, 7))
        },
        limit(vm = this) {
            return vm.maxPeriod || vm.$moment().format('YYYY-MM')
        },
        years(vm = this) {
            const last = Number(vm.limit.slice(0, 4))
            return Array.from({ length: YEARS_BACK + 1 }, (item, i) => last - i)
        },
        months(vm = this) {
            void vm.$i18n.locale
            return Array.from({ length: 12 }, (item, i) => {
                const value = i + 1
                return {
                    value,
                    label: vm.$moment().month(i).format('MMM').replace('.', '').toUpperCase(),
                    disabled: vm.buildPeriod(vm.year, value) > vm.limit,
                }
            })
        },
    },
    methods: {
        buildPeriod(year, month) {
            return `${year}-${String(month).padStart(2, '0')}`
        },
        selectYear(year) {
            const vm = this
            let period = vm.buildPeriod(year, vm.month)
            if (period > vm.limit) period = vm.limit
            if (period !== vm.period) vm.$emit('change', period)
        },
        selectMonth(month) {
            const vm = this
            const period = vm.buildPeriod(vm.year, month)
            if (period !== vm.period) vm.$emit('change', period)
        },
    },
}
</script>

<style scoped>
.period-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    min-width: 0;
}

.period-year {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    height: 28px;
    padding: 0 8px 0 10px;
    flex-shrink: 0;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtPrimary-base);
    font-size: var(--fs-caption);
    font-weight: 700;
}

.period-year:hover {
    border-color: var(--v-accent-base);
}

.period-year-option {
    font-size: var(--fs-caption);
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.period-year-option.active {
    color: var(--v-primary-base);
}

.period-months {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 1 1 auto;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
}

.period-months::-webkit-scrollbar {
    display: none;
}

.period-month {
    flex: 0 0 auto;
    height: 28px;
    padding: 0 9px;
    border-radius: 7px;
    background: transparent;
    color: var(--v-txtMuted-base);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    transition: background 0.15s, color 0.15s;
}

.period-month:hover:not(:disabled) {
    color: var(--v-txtPrimary-base);
    background: var(--v-surface2-base);
}

.period-month.active {
    background: var(--v-surface2-base);
    color: var(--v-primary-base);
}

.period-month:disabled {
    opacity: 0.35;
    cursor: default;
}

.period-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.theme--light .period-month.active {
    color: var(--v-accent-base);
}

.theme--light .period-year-option.active {
    color: var(--v-accent-base);
}
</style>
